import { array, number, object, string } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import { websiteUrlValidation } from "@shared-kernel/primary/forms/validations/website-url";
import { siretValidation } from "@shared-kernel/primary/forms/validations/siret";
import { activityRegistrationNumberValidation } from "@shared-kernel/primary/forms/validations/activity-registration-number";
import { isValidPhoneNumber } from "@shared-kernel/core/utils/phone-validation";
import { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import { trainingSchedulesValidationSchema } from "@academy-context/primary/shared/trainings/training-schedule-form/shared/training-schedule-form.validation";
import { ATTACHMENT_LABEL } from "../types/provider-training-form.types";

/**
 * Shared validation schema for provider information fields.
 * Used by both RequestDataSection (provider) and ExternalProviderBatchRequestForm (student).
 */
export const providerInformationSchema = {
  providerName: string().required(REQUIRED_FIELD).max(255, "Maximum 255 caractères"),
  providerContactFirstName: string().required(REQUIRED_FIELD).max(100, "Maximum 100 caractères"),
  providerContactLastName: string().required(REQUIRED_FIELD).max(100, "Maximum 100 caractères"),
  providerEmail: string().required(REQUIRED_FIELD).email("Email invalide"),
  providerPhone: string()
    .required(REQUIRED_FIELD)
    .test("providerPhone", "Numéro de téléphone invalide", async value => {
      if (value) {
        return await isValidPhoneNumber(value);
      }
      return false;
    }),
  providerSiret: siretValidation(true, "providerCountry"),
  providerCountry: string().required(REQUIRED_FIELD).oneOf(Object.values(SUPPORTED_COUNTRY), "Pays invalide"),
  providerActivityRegistrationNumber: activityRegistrationNumberValidation(),
};

/**
 * Shared validation schema for training details fields.
 * Used by both RequestDataSection (provider) and ExternalProviderBatchRequestForm (student).
 */
export const trainingDetailsSchema = {
  title: string().required(REQUIRED_FIELD).max(255, "Maximum 255 caractères"),
  description: string().required(REQUIRED_FIELD),
  objective: string().required(REQUIRED_FIELD),
  price: number()
    .typeError("Veuillez entrer un prix valide")
    .required(REQUIRED_FIELD)
    .min(1, "Le prix doit être supérieur ou égal à 1 €")
    .max(4000, "Le prix ne peut pas dépasser 4 000 €"),
  websiteUrl: websiteUrlValidation(),
  trainingSchedules: trainingSchedulesValidationSchema,
  attachments: array().of(
    object().shape({
      url: string().required(),
      label: string()
        .oneOf([ATTACHMENT_LABEL.DEVIS, ATTACHMENT_LABEL.PLAQUETTE, ATTACHMENT_LABEL.AUTRE])
        .required("Veuillez sélectionner un type de document"),
    }),
  ),
};

/**
 * Combined base schema for forms that use both provider information and training details.
 */
export const baseProviderTrainingSchema = object().shape({
  ...providerInformationSchema,
  ...trainingDetailsSchema,
});
